import { FaUserAlt } from "react-icons/fa";
import useAuthUser from "../hooks/useAuthUser";
import { useEffect, useState, useRef } from "react";
import Instruksi from "../elements/instruction";
import Potential from "./exam/potential";
import Question from "./exam/question";
import PotensiKerjaLayout from "./exam/specialPotential/potensiKerjaLayout";
import CryptoJS from "crypto-js";
import ExamStep from "../cponents/examStep";
import useSession from "../hooks/useSession";

export default function Exam() {
    const { user } = useAuthUser();
    const [modalOpen, setModalOpen] = useState(false);
    const isTransitioningRef = useRef(false);
    const { session, current, countdown } = useSession(user.version, () => {
        setModalOpen(true);
    });

    useEffect(() => {
        isTransitioningRef.current = false;
        // Never keep instruction modal open if we are not on an instruction session
        if (session.data && session.data[current.data.session]?.type !== "instruction") {
            setModalOpen(false);
        }
    }, [current.data.session, session.data]);

    useEffect(() => {
        if (countdown.duration === 0) {
            if (current.data.session === null) return;
            if (session.data.length === 0) return;

            const sessionType = session.data[current.data.session]?.type;

            // When in an 'exam' session, the active exam component (Question, PotensiKerjaLayout, etc.)
            // handles submitting the user's answers and advancing to nextSession().
            // Calling nextSession() here causes a double advance / skips to the end!
            if (sessionType === 'exam') {
                return;
            }

            nextSession();
        }
    }, [countdown.duration]);

    const nextSession = () => {
        if (current.data.session === null) return false;
        if (session.data.length === 0) return false;
        if (isTransitioningRef.current) {
            console.log('⚠️ nextSession already in progress, ignoring duplicate call');
            return false;
        }
        isTransitioningRef.current = true;
        setTimeout(() => {
            isTransitioningRef.current = false;
        }, 600);

        const currentSessionType = session.data[current.data.session]?.type;
        console.log('➡️ nextSession called, current type:', currentSessionType);

        let nextSessionIndex = current.data.session + 1;
        if (session.data[nextSessionIndex] !== undefined) {
            const nextSessionObj = session.data[nextSessionIndex];
            let nextDuration = Number(nextSessionObj.duration);

            if (nextSessionObj.type === 'break') {
                nextDuration = Math.max(5000, Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 5000);
            } else if (nextSessionObj.type === 'exam') {
                nextDuration = Math.max(1000, Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 60000);
            } else if (nextSessionObj.type === 'instruction') {
                nextDuration = Math.max(1000, Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 60000);
            }

            countdown.setDuration(nextDuration);
            const nextSessionData = {
                session: nextSessionIndex,
                soal: nextSessionObj.type === "exam" ? 0 : null,
            };
            current.set(nextSessionData);

            try {
                localStorage.setItem(
                    "session",
                    CryptoJS.AES.encrypt(
                        JSON.stringify({
                            session: nextSessionData,
                            duration: nextDuration,
                        }),
                        "secret"
                    ).toString()
                );
            } catch (e) {}

            setModalOpen(nextSessionObj.type === "instruction");
            countdown.start();
            return false; // Successfully moved to next session
        } else {
            return true; // No more sessions - exam complete!
        }
    };

    return (
        <>
            <div
                className="container mx-auto py-5"
                style={{ minHeight: "80vh" }}
            >
                <div className="row">
                    <div className="ps-0" style={{ flex: 1 }}>
                        <div className="card" style={{ height: "100%" }}>
                            <div className="card-body">
                                <h5 className="card-title border-bottom pb-3">
                                    Data Peserta
                                </h5>
                                <div className="row mt-3 align-items-center">
                                    <div style={{ width: "25%" }}>
                                        <div
                                            className="border rounded-circle text-white d-flex justify-content-center align-items-center"
                                            style={{
                                                width: "50px",
                                                height: "50px",
                                            }}
                                        >
                                            <FaUserAlt className="h5 text-primary mb-0" />
                                        </div>
                                    </div>
                                    <div style={{ width: "75%" }}>
                                        <h6 className="card-subtitle text-body-secondary">
                                            {user?.nama_lengkap}
                                        </h6>
                                        <p className="card-text">{user?.ttl}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ExamStep session={session} current={current} />
                    <div className="pe-0" style={{ flex: 1 }}>
                        <div className="card" style={{ height: "100%" }}>
                            <div className="card-body">
                                <h5 className="card-title border-bottom pb-3">
                                    Sisa Waktu
                                </h5>
                                <p className="fs-2 fw-bold card-text">
                                    {countdown.time.hours || "00"}:
                                    {countdown.time.minutes || "00"}:
                                    {countdown.time.seconds || "00"}
                                </p>
                            </div>
                        </div>
                    </div>
                    {(!session.data || session.data.length === 0) && (
                        <div className="card mt-4 text-center p-5 w-100 shadow-sm border-0">
                            <h4 className="text-secondary mb-3">Memuat Data Ujian...</h4>
                            <div className="spinner-border text-primary mx-auto mb-3" role="status"></div>
                            <p className="text-muted">
                                Jika halaman tidak termuat atau data kosong, kuota pengerjaan token Anda mungkin sudah habis atau soal belum diaktifkan oleh admin.
                            </p>
                            <div className="mt-3">
                                <a href="/app/dashboard" className="btn btn-outline-primary">
                                    Kembali ke Dashboard
                                </a>
                            </div>
                        </div>
                    )}
                    {session.data &&
                        session.data.length > 0 &&
                        !session.data.some((s) => s.type === "exam") && (
                            <div className="card mt-4 text-center p-5 w-100 shadow-sm border-0">
                                <h4 className="text-danger mb-3">Soal Ujian Belum Tersedia</h4>
                                <p className="text-muted">
                                    Soal untuk subtest ini belum tersedia atau kuota token Anda telah habis. Silakan hubungi admin atau kembali ke dashboard.
                                </p>
                                <div className="mt-3">
                                    <a href="/app/dashboard" className="btn btn-primary">
                                        Kembali ke Dashboard
                                    </a>
                                </div>
                            </div>
                        )}
                    {session.data &&
                        session.data.length > 0 &&
                        current.data.session !== null &&
                        session.data[current.data.session] &&
                        (() => {
                            const currentSession = session.data[current.data.session];
                            const currentSessionName = currentSession?.name;
                            const currentSessionType = currentSession?.type;

                            // Debug logging
                            console.log('Exam Render:', {
                                userVersion: user.version,
                                sessionName: currentSessionName,
                                sessionType: currentSessionType,
                                currentIndex: current.data.session
                            });

                            // Don't render exam content during break or instruction
                            // They are handled by BreakOverlay and Instruksi modal
                            if (currentSessionType === 'break' || currentSessionType === 'instruction') {
                                console.log('⏸️ Break/Instruction active, not rendering exam content');
                                return null;
                            }

                            // SPT Potensi Kerja (Text & Image) - layout is chosen ONLY by Kategori / template / resolved type.
                            // Never by instrument name or question fields (master_type defaults to 'text' on every question).
                            const catStr = String(currentSession?.category || '').toLowerCase().trim();
                            const typeStr = String(currentSession?.psychotest_type || '').toLowerCase().trim();
                            const tplStr = String(currentSession?.option_template || '').toLowerCase().trim();

                            const isSPTPotensiKerja = user.version === 'spesial-potential-test' && (
                                catStr === 'potensi-kerja' ||
                                tplStr.startsWith('potensi-kerja') ||
                                typeStr === 'potensi-kerja'
                            );

                            if (isSPTPotensiKerja) {
                                return (
                                    <PotensiKerjaLayout
                                        session={session}
                                        current={current}
                                        countdown={countdown}
                                        nextSession={nextSession}
                                    />
                                );
                            }

                            // PT Potensi Kerja (Legacy Potential Test / Potensi Kerja) - use Potential component
                            if (user.version !== 'spesial-potential-test' && (user.version === 'potensi-kerja' || currentSessionName === "Potensi Kerja" || (currentSessionName && currentSessionName.toLowerCase().includes('potensi kerja')))) {
                                return (
                                    <Potential
                                        session={session}
                                        current={current}
                                        countdown={countdown}
                                        nextSession={nextSession}
                                    />
                                );
                            }

                            // All other tests - use standard Question component
                            return (
                                <Question
                                    session={session}
                                    current={current}
                                    countdown={countdown}
                                    nextSession={nextSession}
                                />
                            );
                        })()}
                </div>
            </div>
            <Instruksi
                isOpen={modalOpen}
                close={() => {
                    const currentType = session.data?.[current.data.session]?.type;
                    if (currentType === "instruction") {
                        nextSession();
                    } else {
                        setModalOpen(false);
                    }
                }}
                content={
                    session.data &&
                        session.data[current.data.session] !== undefined &&
                        session.data[current.data.session].type === "instruction"
                        ? session.data[current.data.session].content
                        : ""
                }
                countdown={countdown}
            />
            <BreakOverlay
                open={
                    session.data &&
                        session.data[current.data.session] !== undefined &&
                        session.data[current.data.session].type === "break"
                        ? true
                        : false
                }
                countdown={countdown}
                sessionName={session.data && session.data[current.data.session]?.name}
            />
        </>
    );
}

function BreakOverlay({ open, countdown, sessionName }) {
    return (
        open && (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.85)", zIndex: 9999 }}
            >
                <div className="card text-center shadow-lg border-0" style={{ minWidth: "400px", maxWidth: "480px", padding: "2.5rem" }}>
                    <div className="card-body">
                        <h2 className="mb-3">⏸️ Waktu Istirahat</h2>
                        {sessionName && (
                            <p className="text-muted mb-3">
                                Bersiap untuk: <strong className="text-dark">{sessionName}</strong>
                            </p>
                        )}
                        <div className="my-4 py-3 bg-light rounded-3">
                            <h1 className="display-3 fw-bold text-primary mb-1">
                                {countdown?.time?.minutes || "00"}:{countdown?.time?.seconds || "00"}
                            </h1>
                            <p className="text-muted small mb-0">Waktu istirahat tersisa</p>
                        </div>
                        <p className="text-secondary mb-0">
                            Gunakan waktu ini untuk beristirahat sejenak.<br />
                            Tes akan dimulai secara otomatis saat waktu habis.
                        </p>
                    </div>
                </div>
            </div>
        )
    );
}
